<!-- VideoPlayerModal.vue -->
<template>
  <a-modal
    :visible="visible"
    @update:visible="updateVisible"
    :title="'Video Gallery'"
    :width="900"
    :footer="null"
    :destroyOnClose="true"
    class="video-player-modal"
  >
    <div v-if="videos.length" class="video-gallery">
      <!-- Main video player -->
      <div class="main-video-container">
        <div class="navigation-controls">
          <a-button
            @click="previousVideo"
            :disabled="currentIndex === 0"
            type="primary"
            shape="circle"
          >
            <template #icon><LeftOutlined /></template>
          </a-button>
          <span class="video-counter"
            >{{ currentIndex + 1 }} / {{ videos.length }}</span
          >
          <a-button
            @click="nextVideo"
            :disabled="currentIndex === videos.length - 1"
            type="primary"
            shape="circle"
          >
            <template #icon><RightOutlined /></template>
          </a-button>
        </div>

        <div class="video-player">
          <!-- Removed controls attribute -->
          <video
            ref="videoPlayer"
            :src="currentVideo.url"
            class="active-video"
            @play="isPlaying = true"
            @pause="isPlaying = false"
            @ended="isPlaying = false"
            @click="togglePlay"
          ></video>

          <!-- Play/pause overlay -->
          <div class="video-overlay" @click="togglePlay">
            <div class="play-icon" v-if="!isPlaying">
              <PlayCircleFilled class="play-button" />
            </div>
          </div>

          <!-- Custom controls bar -->
          <div class="custom-controls-bar">
            <a-button @click="togglePlay" type="text" class="control-button">
              <template #icon>
                <PauseOutlined v-if="isPlaying" />
                <PlayCircleOutlined v-else />
              </template>
              {{ isPlaying ? 'Pause' : 'Play' }}
            </a-button>

            <div class="progress-container">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${videoProgress}%` }"
                ></div>
              </div>
              <span class="time-display"
                >{{ formatTime(currentTime) }} /
                {{ formatTime(duration) }}</span
              >
            </div>

            <a-button @click="toggleMute" type="text" class="control-button">
              <template #icon>
                <SoundOutlined v-if="!isMuted" />
                <SoundMutedOutlined v-else />
              </template>
            </a-button>

            <a-button
              @click="toggleFullscreen"
              type="text"
              class="control-button"
            >
              <template #icon><FullscreenOutlined /></template>
            </a-button>
          </div>
        </div>

        <div class="video-info">
          <h3>{{ currentVideo.title }}</h3>
          <p>{{ currentVideo.description }}</p>
        </div>
      </div>

      <!-- Thumbnail list -->
      <div class="video-thumbnails">
        <div
          v-for="(video, index) in videos"
          :key="index"
          class="thumbnail-item"
          :class="{ active: currentIndex === index }"
          @click="selectVideo(index)"
        >
          <div class="thumbnail-container">
            <img :src="video.thumbnail" :alt="video.title" />
            <div class="thumbnail-overlay">
              <PlayCircleOutlined v-if="currentIndex !== index" />
              <PlayCircleFilled v-else />
            </div>
          </div>
          <DeleteOutlined class="" @click="deleteVideoFromValidation(video)" />
          <div class="thumbnail-title">{{ video.title }}</div>
        </div>
      </div>
    </div>
    <div v-else class="video-wrapper">
      <div class="video-wrapper__empty">
        <a-empty :description="true" class="video-wrapper__empty-container">
          <template #description>
            <div class="video-empty-text">
              <div class="video-empty-text__title">No Video Found</div>
              <div class="video-empty-text__description">
                No videos were found in this group.
              </div>
            </div>
          </template>
        </a-empty>
      </div>
    </div>
  </a-modal>
</template>

<script>
import {
  DeleteOutlined,
  FullscreenOutlined,
  LeftOutlined,
  PauseOutlined,
  PlayCircleFilled,
  PlayCircleOutlined,
  RightOutlined,
  SoundMutedOutlined,
  SoundOutlined,
} from '@ant-design/icons-vue';
import {} from 'src/config/roles-config';
import TelemetryService from 'src/services/telemetry';
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';

export default defineComponent({
  name: 'VideoPlayerModal',
  components: {
    LeftOutlined,
    RightOutlined,
    PlayCircleOutlined,
    DeleteOutlined,
    PlayCircleFilled,
    PauseOutlined,
    SoundOutlined,
    SoundMutedOutlined,
    FullscreenOutlined,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    videos: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:visible', 'update:videos'],
  setup(props, { emit }) {
    const currentIndex = ref(0);
    const videoPlayer = ref(null);
    const isPlaying = ref(false);
    const isMuted = ref(false);
    const currentTime = ref(0);
    const duration = ref(0);
    const videoProgress = computed(() => {
      return duration.value ? (currentTime.value / duration.value) * 100 : 0;
    });

    // Fix for issue #1: Properly handle modal visibility updates
    const updateVisible = (value) => {
      emit('update:visible', value);
    };

    const currentVideo = computed(() => {
      return (
        props.videos[currentIndex.value] || {
          url: '',
          title: 'No Video',
          description: '',
          thumbnail: '',
        }
      );
    });

    const nextVideo = () => {
      if (currentIndex.value < props.videos.length - 1) {
        currentIndex.value++;
        // Fix for issue #2: Reset to pause state when changing videos
        isPlaying.value = false;
        if (videoPlayer.value) {
          videoPlayer.value.pause();
        }
      }
    };

    const previousVideo = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        // Fix for issue #2: Reset to pause state when changing videos
        isPlaying.value = false;
        if (videoPlayer.value) {
          videoPlayer.value.pause();
        }
      }
    };

    const selectVideo = (index) => {
      if (currentIndex.value === index) {
        togglePlay();
      } else {
        currentIndex.value = index;
        // Fix for issue #2: Reset to pause state when changing videos
        isPlaying.value = false;
        // Don't auto-play, let user initiate playback
      }
    };

    const togglePlay = () => {
      if (!videoPlayer.value) return;

      if (videoPlayer.value.paused) {
        videoPlayer.value.play().catch((err) => {
          console.warn('Play prevented:', err);
        });
      } else {
        videoPlayer.value.pause();
      }
    };

    const toggleMute = () => {
      if (!videoPlayer.value) return;

      videoPlayer.value.muted = !videoPlayer.value.muted;
      isMuted.value = videoPlayer.value.muted;
    };

    const toggleFullscreen = () => {
      const container = videoPlayer.value?.parentElement;
      if (!container) return;

      if (!document.fullscreenElement) {
        container.requestFullscreen().catch((err) => {
          console.warn('Fullscreen request failed:', err);
        });
      } else {
        document.exitFullscreen();
      }
    };

    const formatTime = (seconds) => {
      if (isNaN(seconds) || seconds === Infinity) return '0:00';

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const deleteVideoFromValidation = async (recordId) => {
      try {
        await TelemetryService.removeVideoFromValidation(recordId.id);
        emit(
          'update:videos',
          props.videos.filter((data) => data.id !== recordId.id)
        );
        return;
      } catch (error) {
        console.log(error);
      }
    };

    const updateProgress = () => {
      if (videoPlayer.value) {
        currentTime.value = videoPlayer.value.currentTime;
        duration.value = videoPlayer.value.duration || 0;
      }
    };

    // Handle keyboard shortcuts
    const handleKeyDown = (e) => {
      if (!props.visible) return;

      switch (e.key) {
        case ' ':
        case 'k':
          togglePlay();
          e.preventDefault();
          break;
        case 'ArrowRight':
          if (videoPlayer.value) {
            videoPlayer.value.currentTime += 5;
          }
          e.preventDefault();
          break;
        case 'ArrowLeft':
          if (videoPlayer.value) {
            videoPlayer.value.currentTime -= 5;
          }
          e.preventDefault();
          break;
        case 'n':
          nextVideo();
          e.preventDefault();
          break;
        case 'p':
          previousVideo();
          e.preventDefault();
          break;
        case 'f':
          toggleFullscreen();
          e.preventDefault();
          break;
        case 'm':
          toggleMute();
          e.preventDefault();
          break;
      }
    };

    // Reset current video when modal becomes visible
    watch(
      () => props.visible,
      (newValue) => {
        if (newValue) {
          currentIndex.value = 0;
          isPlaying.value = false;
          currentTime.value = 0;

          // Reset video when modal opens
          if (videoPlayer.value) {
            videoPlayer.value.currentTime = 0;
            videoPlayer.value.load();
          }
        } else {
          // Stop video when modal closes
          if (videoPlayer.value) {
            videoPlayer.value.pause();
          }
        }
      }
    );

    // Update video element when current video changes
    watch(
      () => currentVideo.value,
      () => {
        if (videoPlayer.value) {
          videoPlayer.value.load();
          currentTime.value = 0;
          duration.value = 0;
          // Fix for issue #2: Ensure we're in pause state when video changes
          isPlaying.value = false;
        }
      }
    );

    // Set up event listeners
    onMounted(() => {
      document.addEventListener('keydown', handleKeyDown);
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown);
    });

    // Set up video event listeners when the video element is available
    watch(
      () => videoPlayer.value,
      (newValue) => {
        if (newValue) {
          videoPlayer.value.addEventListener('timeupdate', updateProgress);
          videoPlayer.value.addEventListener('loadedmetadata', updateProgress);
        }
      }
    );

    return {
      currentIndex,
      currentVideo,
      videoPlayer,
      isPlaying,
      isMuted,
      currentTime,
      duration,
      videoProgress,
      nextVideo,
      previousVideo,
      selectVideo,
      togglePlay,
      toggleMute,
      toggleFullscreen,
      formatTime,
      updateVisible, // Export the new method
      deleteVideoFromValidation,
    };
  },
});
</script>

<style scoped>
/* Same styles as before */
.video-gallery {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.main-video-container {
  position: relative;
}

.navigation-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.video-counter {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

.video-player {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.active-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.3s;
}

.video-player:hover .video-overlay {
  opacity: 1;
}

.play-icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.play-button {
  font-size: 64px;
  color: white;
  opacity: 0.8;
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.5));
}

.custom-controls-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 8px 16px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s;
}

.video-player:hover .custom-controls-bar {
  opacity: 1;
}

.control-button {
  color: white !important;
}

.control-button:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0 8px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: #1890ff;
  border-radius: 2px;
  position: absolute;
  top: 0;
  left: 0;
}

.time-display {
  font-size: 12px;
  color: white;
  margin-top: 4px;
}

.video-info {
  margin-top: 16px;
  padding: 0 8px;
}

.video-info h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 500;
}

.video-info p {
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

.video-thumbnails {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 10px 0;
  scrollbar-width: thin;
}

.video-thumbnails::-webkit-scrollbar {
  height: 6px;
}

.video-thumbnails::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.thumbnail-item {
  flex: 0 0 auto;
  width: 160px;
  cursor: pointer;
  transition: all 0.2s;
}

.thumbnail-container {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.thumbnail-group {
  position: relative;
}

.trash-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  color: red;
  font-size: 18px;
}

.thumbnail-item.active .thumbnail-container {
  border-color: #1890ff;
}

.thumbnail-container img {
  width: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
  display: block;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.thumbnail-overlay .anticon {
  font-size: 32px;
  color: white;
}

.thumbnail-item:hover .thumbnail-overlay {
  opacity: 1;
}

.thumbnail-item.active .thumbnail-overlay {
  opacity: 1;
  background: rgba(0, 0, 0, 0.5);
}

.thumbnail-title {
  font-size: 12px;
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 2px;
}

.video-not-found {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 240px;
  background-color: #f0f2f5;
  border-radius: 8px;
}

.video-empty-container {
  padding: 32px 0;
}

.video-empty-container :deep(.ant-empty-image) {
  height: 64px;
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 64px;
  line-height: 64px;
}

.video-empty-text {
  text-align: center;
}

.video-empty-title {
  color: rgba(0, 0, 0, 0.85);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
}

.video-empty-description {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  line-height: 1.6;
}

@media (min-width: 768px) {
  .video-gallery {
    flex-direction: row;
  }

  .main-video-container {
    flex: 1;
  }

  .video-thumbnails {
    flex: 0 0 200px;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 500px;
  }

  .thumbnail-item {
    width: 100%;
  }
}
</style>
